import "./chatpage.scss";
import { h, Component, createRef, Fragment } from "preact";
import { CountdownApplet } from "../../applets/countdown/countdownApplet"
import { PasswordApplet } from "../../applets/password/passwordApplet"
import { useEffect, useState } from "preact/hooks";
import { marked } from "marked";
import { route } from "preact-router";
import { globalUserState, logout } from "../../state/userstate";
import { deleteConversation, getConversation, updateConversation } from "../../services/backend";
import { Container, DropdownButton, DropdownButtonApi, ExpandableColumn, ExpandableColumnsContainer, Menu, MenuItem, Spacer, Spinner, ToolIcon } from "uitoolkit";
import { ChatInput, CommentBlock, Comment, Reasoning, ToolCall } from "components/chat";
import { isMobile, isPWA, getAbsoluteYPosition } from "services/utils";
import { uiToast } from "services/uiservice";
import { GanglionConnection } from "services/websocket/ganglion-connection";
import { Sidebar } from "components/sidebar";
import { availableProviders } from "./models";
import { addConversation, globalUserData, removeConversation, setConversationStarred, setSubject } from "../../state/userdata";
import { TextEditor, tokenizeJs } from "components/texteditor";
import { CanvasRenderScript } from "../../components/embedconvasrenderscript";
import { InfiniteCanvas } from "components/infinitecanvas";
import { type GanglionConversationMessageResponse, type GanglionConversationResponse, isGanglionMessage } from "./messagetypes";
import { setPageTitle } from "hooks";

// brainstorm: all the options from the chatinput moved to a sidepanel to the right? except no it seems "open webui" does it and I dont want to seem like a copycat

const DEFAULT_CHAT_SUBJECT = "Untitled";

export interface IChatPageProps {
    // connection: GanglionConnection2;
    showSendButton?: boolean;
    conversationId?: string | null;
}

interface IChatPageState {
    ganglion: GanglionConnection | null;
    model: string;

    isMobile: boolean;

    isLoading: boolean;
    isWaitingForResponse: boolean;

    conversationId: string | null;
    subject: string | null;
    messages: IChatMessage[];
    personaName: string | null;

    showLeft: boolean;
    showRight: boolean;
}

interface IChatMessage {
    index?: number;         // per-conversation ordinal (present for persisted messages)
    fromUser: boolean;
    model?: string;
    contentType?: string;
    content?: string;
    reasoning?: string;
    component?: string;
    action?: string;
    toolCall?: {
        name: string,
        arguments?: { [id: string]: string }
    },
    time?: Date;
    data?: any;
}

function marshalMessage(msg: GanglionConversationMessageResponse): IChatMessage {
    const result: IChatMessage = {
        fromUser: false,
        content: (msg.message ?? "") + (msg.textDelta ?? ""),
        reasoning: (msg.reasoning ?? "") + (msg.reasoningDelta ?? ""),
        component: msg.component,
        model: msg.model,
        time: msg.receivedTime ? new Date(msg.receivedTime) : new Date(),
        data: msg.data
    };
    if (msg.toolCall) {
        result.toolCall = {
            name: msg.toolCall,
            arguments: msg.toolArguments
        };
    }
    return result;
}

export class ChatPage extends Component<IChatPageProps, IChatPageState> {
    constructor(props: IChatPageProps) {
        super(props);

        this.userStateUnsubscriber = globalUserState.subscribe(userState => {
            if (userState.status === "none") {
                console.log("NOT LOGGED IN");
                route("/");
            }
        });
        if (globalUserState.value.status === "none") {
            console.log("NOT LOGGED IN");
            setTimeout(() => {
                route("/");
            });
        }

        this.state = {
            model: availableProviders[0].models[0].id,
            isLoading: !!props.conversationId,
            isWaitingForResponse: false,
            conversationId: null,
            subject: null,
            personaName: null,
            messages: [],
            ganglion: null,
            isMobile: isMobile(),
            showLeft: false,
            showRight: false
        };

        if (props.conversationId) {
            this.loadMessages(props.conversationId);
        }

        (window as any).toggleLeft = () => this.setState({ showLeft: !this.state.showLeft });
        (window as any).toggleRight = () => this.setState({ showRight: !this.state.showRight });
    }

    dropdownApiRef = createRef<DropdownButtonApi>();
    textfieldRef = createRef<HTMLInputElement>();

    componentDidMount() {
        const ganglion = new GanglionConnection({
            onMessage: (msg) => {
                this.onIncomingMessage(msg);
            },
            onStatus: () => {
                this.onIncomingStatus();
            }
        });
        this.setState({ ganglion });

        window.addEventListener("visibilitychange", this.onVisibilityChanged);
        document.body.className = (document.body.className + " chat").trim();
        setPageTitle("");
    }

    componentWillUnmount() {
        this.state.ganglion?.close();

        window.removeEventListener("visibilitychange", this.onVisibilityChanged);

        document.body.className = document.body.className.replace(" chat", "").trim();
        setPageTitle("");
    }

    componentDidUpdate(prevProps: IChatPageProps) {
        //window.scrollTo(0, document.body.scrollHeight);

        if (prevProps.conversationId !== this.props.conversationId) {
            if (!this.props.conversationId) {
                this.lastMessageIndex = 0;
                this.setState({
                    conversationId: null,
                    messages: [],
                });
                if (this.state.ganglion) {
                    this.state.ganglion.viewConversation(null);
                }
                this.uiSetCurrentSubject("", false); // todo: see if we have the conversationId cached so we can get the title immediately
            } else {
                if (this.state.ganglion?.conversationId !== this.props.conversationId) {
                    this.loadMessages(this.props.conversationId!);
                }
            }
            this.textfieldRef?.current?.focus?.();
        }
    }

    userStateUnsubscriber: (() => void) | null = null;
    textAccumulator: string | null = null;
    reasoningAccumulator: string | null = null;
    lastMessageIndex: number = 0;   // highest per-conversation message index we've merged (catchup cursor)

    onIncomingStatus() {

    }

    onIncomingMessage(msg: GanglionConversationResponse) {
        //console.log("------------ messages: " + this.state.messages.length ,  {...msg});

        console.log(msg);

        if (msg.type == "catchup") {
            // batch of messages newer than our cursor (reconnect gap-fill, or a race between
            // the GraphQL load and attaching to the live stream).
            const incoming = (msg.messages ?? []).filter(m => (m.id ?? 0) > this.lastMessageIndex);
            if (incoming.length) {
                this.lastMessageIndex = incoming.reduce((max, m) => Math.max(max, m.id ?? 0), this.lastMessageIndex);
                this.setState({
                    messages: [
                        ...this.state.messages,
                        ...incoming.map(m => ({
                            index: m.id,
                            fromUser: !!m.fromUser,
                            model: m.model ?? undefined,
                            contentType: m.contentType ?? undefined,
                            content: m.message ?? ""
                        }))
                    ]
                });
                setTimeout(this.scrollLast, 50);
                // advance the connection's reconnect cursor
                this.state.ganglion?.viewConversation(this.state.conversationId ?? null, this.lastMessageIndex);
            }
            return;
        }

        if (msg.type == "conversation-state") {
            if (msg.subject) {
                this.uiSetCurrentSubject(msg.subject, true);
            }
            // todo: we could also patch up the last message, if there is a messagebody
            return;
        }

        if (msg.type == "stall") {
            this.setState({
                isWaitingForResponse: true
            });
        }

        if (msg.type == "user-message") {
            // a message another client/tab sent into this conversation - the sender renders its own
            this.setState({
                messages: [
                    ...this.state.messages,
                    { fromUser: true, content: msg.message ?? "", time: new Date() }
                ]
            });
            setTimeout(this.scrollLast, 50);
            return;
        }

        if (!isGanglionMessage(msg)) return;

        if (msg.conversationId && this.state.conversationId !== msg.conversationId) {
            addConversation(msg.conversationId, DEFAULT_CHAT_SUBJECT);
            this.uiSetCurrentSubject(DEFAULT_CHAT_SUBJECT, true);

            this.setState({
                conversationId: msg.conversationId
            });

            route("/chat/" + msg.conversationId);
        }

        if (msg.action) {
            handleAction(this, msg);
            this.setState({
                isWaitingForResponse: false
            });
        }

        const isDelta = msg.type == "delta" || !!msg.textDelta || !!msg.reasoningDelta;
        const isContent = !!msg.message || !!msg.reasoning;
        const currentMessageIsResponse = this.state.messages.length > 0 && !this.state.messages[this.state.messages.length - 1].fromUser;
        //console.log("currentMessageIsResponse: " + currentMessageIsResponse + ", delta: " + isDelta + ", content: " + isContent);

        if (!currentMessageIsResponse || isContent) {
            //console.log("chat appending new message", marshalMessage(msg));
            this.setState({
                isWaitingForResponse: msg.type == "message-start", // todo: should be getting this from the ganglion-connection statemachine instead
                messages: [
                    ...this.state.messages,
                    marshalMessage(msg)
                ]
            });
            this.textAccumulator = msg.message ?? "";
            this.reasoningAccumulator = msg.reasoning ?? "";
            setTimeout(this.scrollLast, 50);
        }
        else if (!!msg.textDelta || !!msg.toolCall || !!msg.reasoningDelta) {
            //console.log("appending delta", msg.textDelta, msg.reasoningDelta)
            if (msg.textDelta) this.textAccumulator += msg.textDelta;
            if (msg.reasoningDelta) this.reasoningAccumulator += msg.reasoningDelta;
            const messages = [
                ...this.state.messages
            ];
            messages[messages.length - 1] = {
                ...messages[messages.length - 1],
                content: this.textAccumulator ?? "",
                reasoning: this.reasoningAccumulator ?? "",
            };
            if (msg.toolCall) {
                messages[messages.length - 1].toolCall = {
                    name: msg.toolCall,
                    arguments: msg.toolArguments
                };
            }
            this.setState({
                isWaitingForResponse: msg.type !== "message-end",
                messages
            });
        }
        
        if (msg.type == "message-end") {
            this.setState({
                isWaitingForResponse: false // todo: should be getting this from the ganglion-connection statemachine instead
            });
        }
    }

    uiSetCurrentSubject(subject: string, updateConversation: boolean) {
        setPageTitle(subject);

        this.setState({ subject });

        if (updateConversation) {
            setSubject(this.state.conversationId!, subject);
        }
    }

    onVisibilityChanged = () => {
        this.textfieldRef?.current?.focus?.();
    }

    loadMessages(conversationId: string) {
        this.lastMessageIndex = 0;
        this.setState({
            isLoading: true,
            conversationId,
            messages: []
        });
        getConversation(conversationId)
            .then(conversation => {
                if (conversation) {
                    const messages = conversation.messages?.map(m => ({
                        index: m.index,
                        fromUser: !!m.userId,
                        model: m.model ?? undefined,
                        contentType: m.contentType,
                        content: m.content,
                        reasoning: m.reasoning ?? undefined,
                        action: m.action ?? undefined,
                        component: m.component ?? undefined,
                        data: m.jsondata ?? undefined,
                        time: new Date(m.created)
                    })) ?? [];

                    this.lastMessageIndex = messages.reduce((max, m) => Math.max(max, m.index ?? 0), 0);

                    if (this.state.ganglion) {
                        // subscribe to this conversation's live stream (other tabs/clients),
                        // asking only for anything newer than what we just loaded.
                        this.state.ganglion.viewConversation(conversationId ?? null, this.lastMessageIndex);
                    }
                    this.setState({
                        isLoading: false,
                        conversationId: conversationId ?? null,
                        subject: conversation.subject,
                        personaName: conversation.persona?.name ?? null,
                        messages
                    }, () => {
                        this.uiSetCurrentSubject(conversation.subject, false);
                    });
                } else {
                    this.newConversation();
                }
            });
    }

    scrollLast = () => {
        let element = document.querySelector(".chatcontainer > div > .commentcontainer:last-child") as HTMLElement;
        if (element.classList.contains("left")) {
            element = element.previousSibling as HTMLElement;
        }
        const container = document.querySelector(".chatscroll") as HTMLElement;
        if (element && container) {
            const pos = getAbsoluteYPosition(element, false);
            container.scrollTo({
                top: pos - 48,
                behavior: 'smooth'
            });
        }
    };

    loadConversation(conversationId: string) {
        this.dropdownApiRef?.current?.close?.();
        route("/chat/" + conversationId);
    }

    newConversation = () => {
        this.dropdownApiRef?.current?.close?.();
        route("/chat");
    };

    renameConversation = async () => {
        this.dropdownApiRef?.current?.close?.();

        let conv = globalUserData.peek().starredItems?.find(n => n.id == this.state.conversationId);
        conv ??= globalUserData.peek().recentItems?.find(n => n.id == this.state.conversationId);
        if (!conv) return;

        const subj = prompt("Rename conversation", conv.title);
        if (subj) {
            await updateConversation(this.state.conversationId!, { subject: subj });
            this.uiSetCurrentSubject(subj, true);
        }
    };

    toggleStar = async () => {
        this.dropdownApiRef?.current?.close?.();

        const isStarred = !!globalUserData.peek().starredItems?.find(n => n.id == this.state.conversationId);
        await updateConversation(this.state.conversationId!, { starred: !isStarred });
        setConversationStarred(this.state.conversationId!, !isStarred);
    };

    deleteConversation = async () => {
        this.dropdownApiRef?.current?.close?.();

        if (this.state.conversationId && confirm("Delete conversation?")) {
            const id = this.state.conversationId;
            removeConversation(id);
            this.newConversation();
            await deleteConversation(id);
            uiToast("Deleted conversation.");
        }
    };

    onSendButton = (text: string) => {
        //console.log("SEND " + text);
        if (text && this.state.ganglion && !this.state.isWaitingForResponse) {
            if (!this.state.messages || this.state.messages.length == 0) {
                //console.log("setting default subject");
                this.uiSetCurrentSubject(DEFAULT_CHAT_SUBJECT, true);

                // todo: if this is the first message, then also set the model
                //this.state.connection.setModel()
            }

            this.state.ganglion.setModel(this.state.model);
            this.state.ganglion.conversationId = this.state.conversationId;
            this.state.ganglion.sendText(text);
            this.setState({
                isWaitingForResponse: true,
                messages: [
                    ...this.state.messages,
                    {
                        fromUser: true,
                        content: text,
                        time: new Date()
                    }
                ]
            });
            setTimeout(this.scrollLast, 50);
        }
    }

    onStopButton = () => {
        if (this.state.isWaitingForResponse) {
            this.state.ganglion?.sendStop();
        }
    };

    setModel = (model: string) => {
        this.setState({ model });
    };

    render(props: IChatPageProps, state: IChatPageState) {
        const isEmpty = !props.conversationId && !state.messages?.length;
        const isStarred = !!globalUserData.value.starredItems?.find(n => n.id == state.conversationId);

        return (
            <Fragment>
                <Container class="chatapp row grow overflow-y-hidden">
                    <Sidebar
                        page="chat"
                        isEmptyChat={isEmpty}
                        conversationId={state.conversationId}
                        loadConversation={this.loadConversation}
                    />
                    <ExpandableColumnsContainer class="chatandartifact animated grow">
                        <ExpandableColumn hidden={!state.showLeft} class="half">
                            {
                                state.showLeft &&
                                <InfiniteCanvas />
                            }
                            {
                                //<CanvasRenderScript script="visualizer.esm.js" />
                                // <TextEditor tokenizer={tokenizeJs} />
                            }
                        </ExpandableColumn>
                        <ExpandableColumn class="chat chatscroll dividerleft">
                            <div class="sticky top-0 bg-chatfade-top noprint flex-row">
                                {
                                    state.conversationId &&
                                    <DropdownButton variant="text" size="medium" id="anchordropdown1" text={state.subject || "Untitled"} api={this.dropdownApiRef}>
                                        <Menu>
                                            <MenuItem label={isStarred ? "Unstar" : "Star"} onClick={this.toggleStar} />
                                            <MenuItem label="Rename" onClick={this.renameConversation} />
                                            <MenuItem label="Add to project" disabled />
                                            <hr />
                                            <MenuItem label="Delete" onClick={this.deleteConversation} />
                                        </Menu>
                                    </DropdownButton>
                                }
                                <Spacer />
                                {
                                    !isEmpty &&
                                    <ToolIcon icon="addbox" href="/chat" />
                                }
                            </div>
                            <Container class={"chatcontainer maxwidth grow " + (isEmpty ? "content-center" : "content-end")}>
                                {
                                    // page spinner
                                    state.isLoading && !state.messages?.length && <div class="chatspinner">
                                        <StreamingSpinner />
                                    </div>
                                }
                                <div class="flex-column gap-16">
                                    <h2 class="printonly">{state.subject}</h2>
                                    {
                                        // all the messages
                                        state.messages.map((message, index) => <ChatMessage message={message} personaName={state.personaName ?? undefined} spin={state.isWaitingForResponse && !message.fromUser && index == state.messages.length - 1} />)
                                    }
                                    {
                                        // streaming spinner
                                        state.isWaitingForResponse && state.messages.length > 0 && state.messages[state.messages.length - 1].fromUser &&
                                        <CommentBlock side="left">
                                            <StreamingSpinner />
                                        </CommentBlock>
                                    }
                                </div>
                                {
                                    isEmpty && <h2 class="hero"><img src="/icons/favicon-32x32.png" /> <span>A wild machine-intelligence appeared!</span></h2>
                                }
                                <div class={"chatinputcontainer bg-chatfade-bottom" + (isEmpty ? "" : " noprint")}>
                                    <ChatInput
                                        onSend={this.onSendButton}
                                        onStop={this.onStopButton}
                                        buttonType={this.state.isWaitingForResponse ? "stop" : "send"}
                                        placeHolder={isEmpty ? "Ask literally anything" : "Ask a follow-up question"}
                                        minVisibleLines={2}
                                        maxVisibleLines={5}
                                        inputRef={this.textfieldRef}
                                        disableButton={this.state.isLoading}
                                        toolsRight={
                                            <Fragment>
                                                <select class="small" onChange={e => this.setModel(e.currentTarget.value)}>
                                                    {
                                                        availableProviders.map(group => <optgroup label={group.name}>
                                                            {
                                                                group.models.map(model => <option value={model.id} selected={model.id == this.state.model}>{model.name}</option>)
                                                            }
                                                        </optgroup>)
                                                    }
                                                </select>
                                            </Fragment>
                                        }
                                    />
                                    {
                                        isEmpty && <div class="disclaimer">Your machine overlord is definitive. Reality is frequently inaccurate.</div>
                                    }

                                </div>
                            </Container>
                        </ExpandableColumn>
                        <ExpandableColumn hidden={!state.showRight} class="artifact small">
                            <div>References</div>
                        </ExpandableColumn>
                    </ExpandableColumnsContainer >
                </Container >
            </Fragment >
        );
    }
}

function StreamingSpinner() {
    return (
        <Spinner class="tiny" />
    );
}

function ChatMessage({ message, spin, personaName }: { message: IChatMessage, personaName?: string, spin?: boolean }) {
    const htmlObject = useMarkdown(message.content ?? "", message.fromUser ? "text" : "markdown");
    switch (message.component) {
        case null:
        case undefined:
            break;
        case "changepassword":
            return <PasswordApplet {...message.data} />
        case "countdown":
            return <CountdownApplet {...message.data} />
        default:
            return (
                <p>COMPONENT: {message.component}</p>
            );
    }
    if (message.action || message.component) {
        if (message.action) {
            return <div class="action">{message.action}</div>
        }
    }

    return (
        <CommentBlock
            side={message.fromUser ? "right" : "left"}
            name={message.fromUser ? undefined : ((personaName ? `${personaName} / ` : "") + (message.model ? message.model : "Exocortex"))}
            time={formatTime(message.time)}
        >
            {
                message.reasoning &&
                <Reasoning text={message.reasoning} />
            }
            {
                message.toolCall &&
                <ToolCall text={message.toolCall.name + (message.toolCall.arguments ? ` (${Object.entries(message.toolCall.arguments).map(entry => entry[0] + ": " + entry[1]).join(", ")})` : "")} />
            }
            {
                message.content &&
                <Comment html={htmlObject} />
            }
            {
                spin && <Spinner class="tiny" />
            }
        </CommentBlock>
    );
}

function formatTime(date: Date | undefined | null) {
    if (!date) return undefined;
    return ("0" + date.getHours()).slice(-2) + ":" + ("0" + date.getMinutes()).slice(-2); // + ":" + ("0" + date.getSeconds()).slice(-2);
}

function handleAction(page: ChatPage, message: GanglionConversationMessageResponse) {
    switch (message.action?.toLowerCase()) {
        case "logout":
            logout();
            break;
        case "quit":
            window.close();
            break;
        default:
            // todo: try to load the action from an external ESM
            alert("action: " + message.action);
    }
}

function useMarkdown(content: string, type: "markdown" | "text" = "markdown") {
    const [result, setResult] = useState<{ __html: string } | undefined>((!content || type == "markdown") ? { __html: "" } : {
        __html: content.replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;')
            .replace(/  /g, ' &nbsp;')
            .replace(/\n/g, "<br />")
    });
    useEffect(() => {
        if (content && type == "markdown") {
            const run = async () => {
                const res = await marked(content, {
                    breaks: true,
                    gfm: true
                });
                setResult({ __html: res });
            };
            run();
        }
    }, [content])
    return result;
}
